// Resolución de la estructura de un torneo (zonas + equipos) importada desde Excel.
//
// El archivo trae una solapa por zona con los nombres tal como los escribe la planilla;
// el server sólo los lee (routes/torneos.js) y acá se los busca entre los equipos
// cargados en el sistema. El criterio es el mismo que en la importación de partidos:
// match exacto sobre el nombre normalizado y, si no hay, la sugerencia del más parecido
// para que la confirme el usuario. Nunca se resuelve solo un nombre que no coincide
// exacto: los nombres reales se solapan de a pares (MUVI AZUL/BLANCO/CELESTE, TDH B/D,
// CRUZ DEL SUR A/B) y armar la zona con el equipo equivocado no se nota hasta que es
// tarde.

import { normalizarNombre, similitudNombres, UMBRAL_SUGERENCIA } from "./importFixture";

// Equipos entre los que se puede elegir: los que ya están en el torneo más los que
// siguen disponibles en el select. Hay que mirar las dos listas porque TeamsScreen va
// sacando de `equipos` los que se agregan.
export function catalogoDeEquipos(equipos = [], teams = []) {
  const porId = new Map();
  teams.forEach((t) => porId.set(String(t.id), { id: String(t.id), nombre: t.name, enTorneo: true }));
  equipos.forEach((e) => {
    const id = String(e.value);
    if (!porId.has(id)) porId.set(id, { id: id, nombre: e.label, enTorneo: false });
  });
  return Array.from(porId.values()).map((c) => ({ ...c, normalizado: normalizarNombre(c.nombre) }));
}

// Un nombre del Excel contra el catálogo.
//   resuelto -> { equipoId, nombre }        (coincidencia exacta, no se pregunta nada)
//   sin resolver -> { equipoId: "", ranking, sugerencia }  lo elige el usuario
function resolverNombre(nombreExcel, catalogo) {
  const buscado = normalizarNombre(nombreExcel);
  const exacto = catalogo.find((c) => c.normalizado === buscado);
  if (exacto) {
    return { nombreExcel: nombreExcel, equipoId: exacto.id, nombre: exacto.nombre, enTorneo: exacto.enTorneo };
  }

  const ranking = catalogo
    .map((c) => ({ id: c.id, nombre: c.nombre, score: similitudNombres(nombreExcel, c.nombre) }))
    .sort((a, b) => b.score - a.score);
  const mejor = ranking[0];

  return {
    nombreExcel: nombreExcel,
    equipoId: "",
    ranking: ranking,
    sugerencia: mejor && mejor.score >= UMBRAL_SUGERENCIA ? mejor : null,
  };
}

// zonasExcel: [{ zona, equipos: [nombre] }] tal como las devuelve el server.
export function resolverEstructura(zonasExcel = [], catalogo = []) {
  return zonasExcel.map((zona, indice) => ({
    clave: "zona_" + indice,
    nombre: zona.zona,
    equipos: (zona.equipos || []).map((nombre) => resolverNombre(nombre, catalogo)),
  }));
}

// Clave del ajuste manual de un nombre sin resolver.
export const claveDeEquipo = (zona, equipo) => zona.clave + "|" + equipo.nombreExcel;

// Zonas listas para crear, con los ajustes del usuario ya aplicados.
//
// Un mismo equipo no puede estar en dos zonas de la misma rueda (es lo que valida
// ZoneScreen con takenByOtherZones), así que si un nombre cae dos veces queda en la
// primera zona y la repetición se informa en vez de romper la rueda.
export function armarZonas(resolucion = [], ajustes = {}) {
  const asignados = new Map();

  return resolucion.map((zona) => {
    const teamIds = [];
    const repetidos = [];
    const pendientes = [];

    zona.equipos.forEach((equipo) => {
      const elegido = equipo.equipoId || ajustes[claveDeEquipo(zona, equipo)] || "";
      if (!elegido) {
        pendientes.push(equipo.nombreExcel);
        return;
      }
      if (asignados.has(elegido)) {
        repetidos.push({ nombreExcel: equipo.nombreExcel, zona: asignados.get(elegido) });
        return;
      }
      asignados.set(elegido, zona.nombre);
      teamIds.push(elegido);
    });

    return { clave: zona.clave, name: zona.nombre, teamIds: teamIds, repetidos: repetidos, pendientes: pendientes };
  });
}
